/**
 * Appels du journal (ARCHITECTURE.md §5.1, specs §6.2 et §6.3).
 *
 * ⚠️ **Toutes** ces mutations renvoient la journée complète. C'est la convention
 * de l'API (cf. `server/src/routes/days.ts`) et c'est ce qui permet de
 * réconcilier en une fois la mise à jour optimiste du bandeau de restant (§6.2),
 * sans second aller-retour.
 *
 * ⚠️ Le client n'envoie **jamais** de total : il décrit l'entrée, le serveur
 * décide des macros écrites (§5.1). Le type `LogEntryInput` n'a d'ailleurs aucun
 * champ de total à offrir.
 */

import type {
  AdjustEntryIngredientsBody,
  ApplyTemplateBody,
  CopyMealBody,
  CreateLogEntryBody,
  CreateMealBody,
  DayDTO,
  LogEntryInput,
  UpdateMealBody,
} from '../../../shared/types';
import { apiFetch } from './client';

function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/*
 * ⚠️ **Il n'y a plus de `fetchToday()`.** `GET /api/days/today` borne la journée
 * par le fuseau du **serveur** (`TZ`, §9) ; l'écran doit suivre celui de
 * l'appareil — demandé après usage, et c'est le comportement juste dès qu'on
 * voyage. La route existe toujours côté serveur, où elle garde tout son sens
 * (c'est ce fuseau-là qui pilotera les notifications de l'étape 9), mais le
 * front n'a plus à la connaître : il calcule sa date et demande cette date.
 */

/** Journée d'une date donnée. La crée si elle n'existe pas encore (§4.5). */
export function fetchDay(date: string): Promise<DayDTO> {
  return apiFetch<DayDTO>(`/api/days/${date}`);
}

/**
 * Recopie les entrées d'un repas déjà composé dans celui-ci.
 *
 * ⚠️ Le client ne transmet **que** l'identifiant du repas source : c'est le
 * serveur qui relit ses entrées et décide de ce qu'il écrit (§5.1). Rien de ce
 * qui s'affiche à l'écran d'ajout ne remonte ici.
 */
export function copyMeal(mealId: number, body: CopyMealBody): Promise<DayDTO> {
  return send<DayDTO>('POST', `/api/meals/${String(mealId)}/copy`, body);
}

/** Nouveau snapshot depuis un modèle, sur une journée déjà créée. */
export function applyTemplate(date: string, body: ApplyTemplateBody): Promise<DayDTO> {
  return send<DayDTO>('POST', `/api/days/${date}/apply-template`, body);
}

/* --- Repas : libres, sans effet sur `day_template` (specs §6.2) ---------- */

export function createMeal(date: string, body: CreateMealBody): Promise<DayDTO> {
  return send<DayDTO>('POST', `/api/days/${date}/meals`, body);
}

export function updateMeal(mealId: number, body: UpdateMealBody): Promise<DayDTO> {
  return send<DayDTO>('PATCH', `/api/meals/${String(mealId)}`, body);
}

export function deleteMeal(mealId: number): Promise<DayDTO> {
  return send<DayDTO>('DELETE', `/api/meals/${String(mealId)}`);
}

/* --- Entrées de journal — le chemin critique ---------------------------- */

export function createLogEntry(mealId: number, body: CreateLogEntryBody): Promise<DayDTO> {
  return send<DayDTO>('POST', `/api/meals/${String(mealId)}/entries`, body);
}

export function updateLogEntry(entryId: number, body: LogEntryInput): Promise<DayDTO> {
  return send<DayDTO>('PATCH', `/api/entries/${String(entryId)}`, body);
}

export function deleteLogEntry(entryId: number): Promise<DayDTO> {
  return send<DayDTO>('DELETE', `/api/entries/${String(entryId)}`);
}

/**
 * Correction des quantités d'un **bloc recette** déjà journalisé (specs §6.5-d).
 *
 * ⚠️ Route dédiée, et non `updateLogEntry` : ce qu'on corrige, ce sont les lignes
 * du bloc, désignées par leur `id` de journal. Le serveur recalcule les macros de
 * ces lignes puis le total de l'entrée parente — **sans toucher à la recette
 * source**, ni à aucune autre entrée.
 */
export function adjustLogEntryIngredients(
  entryId: number,
  body: AdjustEntryIngredientsBody,
): Promise<DayDTO> {
  return send<DayDTO>('PATCH', `/api/entries/${String(entryId)}/ingredients`, body);
}
